// Catalogs shared by the ACCESS simulator tab's editors.
//
// The operation ids, result codes and tracks are the wire values of
// cmmiaccess/lib (types.go: Operation, EligibilityResult, AlignmentResult,
// UnalignmentResult, ReportDataResult, Track) — the simulator answers with them
// verbatim, so the portal must offer exactly these and nothing else.

export const OP_CHECK_ELIGIBILITY = 'check_eligibility';
export const OP_ALIGN = 'align';
export const OP_UNALIGN = 'unalign';
export const OP_REPORT_DATA = 'report_data';
export const OP_OAUTH = 'oauth';

export const PATIENT_OPERATIONS = [OP_CHECK_ELIGIBILITY, OP_ALIGN, OP_UNALIGN, OP_REPORT_DATA];

export const MODE_RESULT = 'result';
export const MODE_HTTP_ERROR = 'http_error';
export const MODE_STILL_PROCESSING = 'still_processing_forever';
// 'no_record' is only meaningful as an org default: the simulator
// answers with the built-in "this MBI is unknown to CMS" business result.
export const MODE_NO_RECORD = 'no_record';

export const PATIENT_MODES = [MODE_RESULT, MODE_HTTP_ERROR, MODE_STILL_PROCESSING];
export const DEFAULT_MODES = [...PATIENT_MODES, MODE_NO_RECORD];

export const FAIL_AT_OPTIONS = ['accept', 'poll'];

export const PATIENT_STATUSES = ['new', 'in_flight', 'done', 'parked'];

export const TRACKS = ['CKM', 'eCKM', 'MSK', 'BH'];

// Unknown result codes are rejected by our parsers and routed to manual review.
// The simulator must offer them as choices too, so testers can
// exercise the manual-review path. Each operation gets one representative
// unknown code; the value is arbitrary (the backend stores any string).
export const UNKNOWN_RESULT_CODE = 'future-eligibility-code';

export const RESULT_CODES = {
  [OP_CHECK_ELIGIBILITY]: [
    'eligible',
    'eligible-pending-diagnosis',
    'not-eligible-not-medicare',
    'not-eligible-mismatch',
    'not-eligible-diagnoses',
    'not-eligible-clinical-exclusion',
    'not-eligible-control-group',
    'not-eligible-services',
    'not-eligible-already-aligned',
    UNKNOWN_RESULT_CODE,
  ],
  [OP_ALIGN]: [
    'aligned',
    'aligned-switch-approved',
    'not-aligned-mismatch',
    'not-aligned-diagnoses',
    'not-aligned-clinical-exclusion',
    'not-aligned-control-group',
    'not-aligned-already-aligned',
    'not-aligned-no-switch-attestation',
    'not-aligned-not-medicare',
    'not-aligned-services',
    UNKNOWN_RESULT_CODE,
  ],
  [OP_UNALIGN]: [
    'unaligned',
    'unalignment-pending',
    'cannot-unalign-during-lock-in',
    'patient-not-aligned',
    UNKNOWN_RESULT_CODE,
  ],
  [OP_REPORT_DATA]: [
    'success',
    'incomplete',
    'incomplete-data',
    'duplicate',
    'validation-error',
    'reporting-period-closed',
    'patient-not-aligned',
    UNKNOWN_RESULT_CODE,
  ],
  // The 'oauth' org default row: mode='result' picks the credential policy,
  // mode='http_error' simulates a token-endpoint outage.
  [OP_OAUTH]: ['lenient', 'strict'],
};

// The CMS happy path the simulator answers for an org that was never
// configured: the "good" result code of each operation.
export const HAPPY_PATH = {
  [OP_CHECK_ELIGIBILITY]: 'eligible',
  [OP_ALIGN]: 'aligned',
  [OP_UNALIGN]: 'unaligned',
  [OP_REPORT_DATA]: 'success',
  [OP_OAUTH]: 'lenient',
};

export const emptyScript = () => ({
  mode: MODE_RESULT,
  fail_at: 'accept',
  result_code: null,
  result_display: null,
  http_status: null,
  error_body: null,
  poll_202_count: 0,
  times_remaining: null,
});

// scriptFrom builds the editor draft out of a stored script row, or out of the
// org default shown as a placeholder.
export const scriptFrom = (script, fallback) => ({
  ...emptyScript(),
  ...(script || {}),
  result_code: script?.result_code ?? fallback?.result_code ?? null,
  poll_202_count: script?.poll_202_count ?? 0,
});

// scriptSummary is the one-line description of a stored script:
// "eligible", "500", "503 @poll", "202" (never finishes), "404" (no such patient).
export const scriptSummary = script => {
  if (!script) return null;
  switch (script.mode) {
    case MODE_HTTP_ERROR:
      return `${script.http_status ?? ''}${script.fail_at === 'poll' ? ' @poll' : ''}`;
    case MODE_STILL_PROCESSING:
      return '202';
    case MODE_NO_RECORD:
      return '404';
    default:
      return script.result_code || script.mode;
  }
};

export const toOptions = values => values.map(value => ({ value, label: value }));

// CMS MBI shape (patients/types/mbi.go): 11 chars, alpha positions exclude
// S, L, O, I, B, Z. The "generate random MBI" helper produces a
// value in this exact shape so the simulator's eligibility check accepts it.
const MBI_DIGITS = '0123456789';
const MBI_ALPHA = 'ACDEFGHJKMNPQRTUVWXY';
const MBI_ALNUM = `${MBI_DIGITS}${MBI_ALPHA}`;
const MBI_FIRST = '123456789';

const randomOf = set => set[Math.floor(Math.random() * set.length)];

export const generateRandomMbi = () => (
  randomOf(MBI_FIRST)
  + randomOf(MBI_ALPHA)
  + randomOf(MBI_ALNUM)
  + randomOf(MBI_DIGITS)
  + randomOf(MBI_ALPHA)
  + randomOf(MBI_ALNUM)
  + randomOf(MBI_DIGITS)
  + randomOf(MBI_ALPHA)
  + randomOf(MBI_ALPHA)
  + randomOf(MBI_DIGITS)
  + randomOf(MBI_DIGITS)
);
